import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";

type DroneBattery = {
  id: string;
  name: string;
  serialNumber: string;
  createdAt: string;
};

type Drone = {
  id: string;
  name: string;
  batteries: DroneBattery[] | null;
};

type BatteryUsage = {
  batteryId: string;
  name: string;
  serialNumber: string;
  percentBefore?: number;
  percentAfter?: number;
};

type LogbookEntry = {
  id: string;
  droneId: string;
  flightDate: string;
  locationName: string | null;
  preflight?: { batteryUsage?: BatteryUsage[] } | null;
  debriefing?: { batteryUsage?: BatteryUsage[] } | null;
};

const DASH = "—";

function fmtDate(iso?: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function BatteryDetail() {
  const { droneId, batteryId } = useParams<{ droneId: string; batteryId: string }>();

  const { data: drones = [], isLoading: dronesLoading } = useQuery<Drone[]>({
    queryKey: ["/api/drones"],
  });
  const { data: logbook = [], isLoading: logbookLoading } = useQuery<LogbookEntry[]>({
    queryKey: ["/api/logbook"],
  });

  const drone = drones.find((d) => d.id === droneId);
  const battery = drone?.batteries?.find((b) => b.id === batteryId);

  const history = logbook.filter((e) => {
    if (e.droneId !== droneId) return false;
    const pre = e.preflight?.batteryUsage || [];
    const post = e.debriefing?.batteryUsage || [];
    return (
      pre.some((b) => b.batteryId === batteryId) ||
      post.some((b) => b.batteryId === batteryId)
    );
  });

  const cycles = history.length;

  let sum = 0;
  let count = 0;
  for (const e of history) {
    const pre = e.preflight?.batteryUsage?.find((u) => u.batteryId === batteryId);
    const post = e.debriefing?.batteryUsage?.find((u) => u.batteryId === batteryId);
    if (typeof pre?.percentBefore === "number" && typeof post?.percentAfter === "number") {
      const diff = pre.percentBefore - post.percentAfter;
      if (Number.isFinite(diff) && diff >= 0) {
        sum += diff;
        count += 1;
      }
    }
  }
  const avgDischarge = count > 0 ? Math.round(sum / count) : null;

  const loading = dronesLoading || logbookLoading;

  if (loading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Akku wird geladen …</div>;
  }

  return (
    <div className="space-y-6" data-testid="screen-battery-detail">
      <div>
        <Link
          to={droneId ? `/drohnen/${droneId}/info` : "/drohnen"}
          className="text-sm text-brand-600 hover:underline"
        >
          ← Zurück zur Drohne
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
          <span className="text-2xl font-bold text-brand-600">B</span>
        </div>
        <div className="min-w-0">
          <p className="text-lg font-semibold text-[var(--color-app-text)]" data-testid="text-battery-name">
            {battery?.name ?? "Akku"}
          </p>
          <p className="text-sm text-[var(--color-app-text-secondary)] truncate" data-testid="text-battery-subtitle">
            {drone?.name ? `${drone.name} · ` : ""}SN: {battery?.serialNumber || DASH}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-2 divide-x divide-[var(--color-app-border)]">
          <div className="text-center px-3">
            <div className="text-xs uppercase text-[var(--color-app-text-secondary)] font-medium">Flüge</div>
            <div className="text-2xl font-bold text-[var(--color-app-text)] mt-1" data-testid="text-battery-flights">
              {cycles}
            </div>
          </div>
          <div className="text-center px-3">
            <div className="text-xs uppercase text-[var(--color-app-text-secondary)] font-medium">Ø Verbrauch</div>
            <div className="text-2xl font-bold text-[var(--color-app-text)] mt-1" data-testid="text-battery-avg">
              {avgDischarge !== null ? `${avgDischarge}%` : DASH}
            </div>
          </div>
        </div>
        {!battery ? (
          <p className="text-sm italic text-[var(--color-caution)] mt-4" data-testid="text-battery-missing">
            Dieser Akku ist nicht mehr in den Drohnen-Einstellungen vorhanden. Bestehende
            Logbuch-Einträge bleiben verlinkt.
          </p>
        ) : null}
      </div>

      <div>
        <h2 className="text-base font-semibold text-[var(--color-app-text)] mb-3">Flughistorie</h2>
        {history.length === 0 ? (
          <div className="card text-center py-10 text-[var(--color-app-text-secondary)]" data-testid="empty-battery-history">
            Noch keine Flüge mit diesem Akku im Logbuch.
          </div>
        ) : (
          <ul className="space-y-2">
            {history.map((entry) => {
              const pre = entry.preflight?.batteryUsage?.find((u) => u.batteryId === batteryId);
              const post = entry.debriefing?.batteryUsage?.find((u) => u.batteryId === batteryId);
              return (
                <li key={entry.id}>
                  <Link
                    to={`/logbuch/${entry.id}`}
                    className="flex items-center gap-3 bg-white border border-[var(--color-app-border)] rounded-lg px-4 py-3 hover:bg-[var(--color-app-bg-secondary)]"
                    data-testid={`battery-history-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[var(--color-app-text)]">
                        {fmtDate(entry.flightDate)}
                      </div>
                      <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
                        {entry.locationName || "Ohne Ort"}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {typeof pre?.percentBefore === "number" ? (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[var(--color-go-bg)] text-[var(--color-go)]">
                            Start {pre.percentBefore}%
                          </span>
                        ) : null}
                        {typeof post?.percentAfter === "number" ? (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[var(--color-caution-bg)] text-[var(--color-caution)]">
                            Ende {post.percentAfter}%
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span className="text-[var(--color-app-text-tertiary)] shrink-0">→</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
